import React, { ReactNode, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Tooltip } from '@mui/material';
import { SearchFilterViewsType } from 'js/constants/visibilityFilters';
import { isOwner, ownershipLabel, shouldDisplayOwnershipLabel } from 'js/lib/ACLUtils';
import { selectCard } from 'js/actions';
import CredentialDetailActionsContainer from 'js/components/credentials/cards/CredentialDetailActionsContainer';
import DataCardList from 'new/elements/DataCardList/DataCardList';
import { useListOldReduxContainer } from 'new/elements/DataCard/useListOldReduxContainer';
import DateUtils from 'js/lib/DateUtils';
import { ChipWithCopy } from 'new/elements/ChipWithCopy';
import DataCard, { DataCardCell, NameDescCell } from 'new/elements/DataCard';
import LightningStatus from 'new/elements/LightningStatus';
import { CredentialsDetailHeader } from 'js/components/credentials/cards/CredentialsDetailHeader';
import { AvailableFilters } from 'js/components/MacroInput/types/AvailableFilters';
import Typography from 'new/elements/Typography';
import { mapStateToProps } from 'js/components/credentials/cards/stateMapper';
import { Stack } from 'new/elements/Stack';
import styles from './CredentialsTable.module.scss';
import { ConnectionTypeChip } from 'new/elements/ConnectionTypeChip/ConnectionTypeChip';

type Props = {
  urlId?: string;
  data: any[];
  isLoading: boolean;
  availableFilterTypes: string[];
  getSourceMeta: (id: number) => any;
  loadData: (page: number, sortCriteria: string, availableFilters: AvailableFilters) => void;
  view: SearchFilterViewsType;
  noContentView?: ReactNode;
  detailViewTabs: any[];
};

const makeRowRenderer =
  ({ handleCardSelected, getData }) =>
  (index, item) => {
    const {
      cardData,
      name,
      readOnly,
      status,
      owner,
      createdAt,
      connectionName,
      connectionIcon,
      disableEditing,
      disableEditMessage,
    } = getData(index);

    const credentialStatus = status || cardData?.validStatus;
    const onClickCard = () => handleCardSelected(cardData);

    return (
      <DataCard
        key={item.id || index}
        className={styles.cardContainer}
        onClick={onClickCard}
        rowsSlots={[
          <>
            <NameDescCell name={name} className={styles.nameCardCell} />,
            <DataCardCell rightCTAsCell className={styles.credentialCardCell}>
              <LightningStatus status={credentialStatus} />
              <CredentialDetailActionsContainer
                data={cardData}
                readOnly={readOnly}
                openDetailsModal={onClickCard}
                disableEditing={disableEditing}
                disableEditMessage={disableEditMessage}
              />
            </DataCardCell>
          </>,
          <DataCardCell multiCol className={styles.credentialCardCell}>
            <Stack gap="16px" direction="row" alignItems="center" justifyContent="flex-end">
              <Tooltip title={connectionName} arrow>
                <Stack className={styles.resourceInfo}>
                  <ConnectionTypeChip
                    icon={connectionIcon}
                    label={connectionName}
                    style={{ border: 'none', padding: 0 }}
                  />
                </Stack>
              </Tooltip>
              <ChipWithCopy tooltipTitle="Copy Credential ID" toCopy={cardData.id}>
                ID {cardData.id}
              </ChipWithCopy>
            </Stack>
            <Stack alignItems="flex-end" justifyContent="center" gap="4px">
              <Typography variant="body3">Created on {createdAt}</Typography>
              {!isOwner(cardData) && (
                <div className={styles.credentialCardCell}>
                  <Typography variant="body3">
                    Owned by {(owner && owner.full_name) || owner.email}
                  </Typography>
                </div>
              )}
            </Stack>
          </DataCardCell>,
        ].filter(Boolean)}
      />
    );
  };

const CredentialsTable = ({
  availableFilterTypes,
  view,
  urlId,
  isLoading,
  data,
  detailViewTabs,
  getSourceMeta,
  loadData,
  noContentView,
}: Props) => {
  const dispatch = useDispatch();
  const listing = useSelector((state: any) => state.app.listing);
  const { selectedCardId, aclQueryMode } = listing;
  const handleCardSelected = useCallback((row) => {
    dispatch(selectCard(row.id));
  }, []);
  useEffect(() => {
    if (urlId) {
      handleCardSelected({ id: urlId });
    }
  }, []);

  const getData = useListOldReduxContainer(
    data,
    mapStateToProps,
    (ownProps) => {
      const {
        cardData,
        connectionInfo,
        connectionOrVendorType,
        readOnly,
        adminAccess,
        isManaged,
        owner,
      } = ownProps;
      const createdAt = DateUtils.convertDateStringToFormat(cardData.created_at, 'MMM D, YYYY');
      const name = cardData.name || '(Untitled)';
      const description = cardData.description || ' ';

      let connectionIcon;
      let connectionName;

      if (connectionInfo) {
        connectionIcon = connectionInfo.iconSm;
        connectionName = connectionInfo.name;
      } else {
        connectionName = connectionOrVendorType;
      }

      const validStatus = cardData.validStatus;

      const disableEditing = isManaged;
      const disableEditMessage = disableEditing
        ? 'This credential is managed by Nexla. Please contact support@nexla.com for editing this credential.'
        : null;

      return {
        cardData,
        name,
        description,
        readOnly,
        adminAccess,
        status: validStatus,
        owner,
        createdAt,
        connectionName,
        connectionIcon,
        disableEditing,
        disableEditMessage,
      };
    },
    [aclQueryMode, getSourceMeta],
  );

  return (
    <DataCardList
      data={data}
      view={view}
      availableFilterTypes={availableFilterTypes}
      detailViewTabs={detailViewTabs}
      urlId={urlId}
      loading={isLoading}
      detailHeaderContent={
        <CredentialsDetailHeader
          {...getData(data.findIndex((item: any) => item.id === selectedCardId))}
        />
      }
      rowRenderer={makeRowRenderer({ handleCardSelected, getData })}
      getSourceMeta={getSourceMeta}
      noContentView={noContentView}
      loadData={loadData}
    />
  );
};

export default CredentialsTable;
